import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { FaWhatsapp, FaPhoneAlt, FaEnvelope } from "react-icons/fa";
import { useSiteSettings } from "../contexts/SiteSettingsContext";

const whatsappMessage =
  "Hello! I'm interested in discussing the language programs you provide.";

export default function FloatingContactButton() {
  const [isOpen, setIsOpen] = useState(false);
  const { settings } = useSiteSettings();

  // Derive WhatsApp and email links from settings
  const whatsappLink = useMemo(() => {
    const number = settings.whatsapp?.replace(/[^0-9]/g, "") || "919049830590";
    return `https://wa.me/${number}?text=${encodeURIComponent(whatsappMessage)}`;
  }, [settings.whatsapp]);

  const mailLink = useMemo(() => {
    return `mailto:${settings.email || "enquiry@thelinguaalliance.com"}`;
  }, [settings.email]);

  // radius of the arc (px) — increase to spread buttons further
  const radius = 90;

  // angles in degrees for quarter circle (0 = up, 90 = left)
  const angles = [0, 45, 90];

  // compute transform positions from angles
  const getTransform = (angleDeg) => {
    const rad = (angleDeg * Math.PI) / 180;
    const x = -Math.cos(rad) * radius; // negative because we move left from origin
    const y = -Math.sin(rad) * radius; // negative because we move up from origin
    return { x, y };
  };

  const handleToggle = (e) => {
    e.stopPropagation();
    setIsOpen((s) => !s);
  };

  // close menu when clicking one of the actions
  const handleActionClick = () => setIsOpen(false);

  return (
    <div
      className="fixed bottom-6 right-4 z-50"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
      // allow keyboard focus to open/close
      onFocus={() => setIsOpen(true)}
      onBlur={() => setIsOpen(false)}
    >
      {/* wrapper that defines the interactive area */}
      <div className="relative w-[140px] h-[140px]">
        {/* action buttons container (absolute relative to wrapper) */}
        <div
          aria-hidden={!isOpen}
          className="absolute bottom-3 right-3 w-0 h-0 pointer-events-none"
          style={{ zIndex: 30 }}
        >
          {angles.map((angle, idx) => {
            const { x, y } = getTransform(angle);
            // choose icon per index: 0 -> Phone (internal), 1 -> Email, 2 -> WhatsApp
            const item = [
              {
                node: (
                  <Link
                    to="/contact"
                    className="flex items-center justify-center w-12 h-12 rounded-full bg-white text-primary shadow-lg"
                    onClick={handleActionClick}
                    title="Contact"
                  >
                    <FaPhoneAlt size={18} />
                  </Link>
                ),
              },
              {
                node: (
                  <a
                    href={mailLink}
                    className="flex items-center justify-center w-12 h-12 rounded-full bg-white text-primary shadow-lg"
                    onClick={handleActionClick}
                    title="Email"
                  >
                    <FaEnvelope size={18} />
                  </a>
                ),
              },
              {
                node: (
                  <a
                    href={whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-12 h-12 rounded-full bg-white text-[#25D366] shadow-lg"
                    onClick={handleActionClick}
                    title="WhatsApp"
                  >
                    <FaWhatsapp size={18} />
                  </a>
                ),
              },
            ][idx];

            return (
              <div
                key={angle}
                // start at origin (0,0) and move to computed x,y when open
                style={{
                  position: "absolute",
                  right: 0,
                  bottom: 0,
                  transform: isOpen
                    ? `translate(${x}px, ${y}px)`
                    : `translate(0px, 0px)`,
                  transition:
                    "transform 260ms cubic-bezier(.2,.9,.2,1), opacity 200ms ease, visibility 200ms ease",
                  opacity: isOpen ? 1 : 0,
                  visibility: isOpen ? "visible" : "hidden",
                  pointerEvents: isOpen ? "auto" : "none",
                }}
              >
                {item.node}
              </div>
            );
          })}
        </div>

        {/* Main contact button */}
        <button
          type="button"
          aria-expanded={isOpen}
          aria-label="Contact options"
          onClick={handleToggle}
          className="absolute bottom-0 right-0 inline-flex items-center justify-center rounded-full bg-gradient-to-r from-primary to-primary-soft w-14 h-14 text-white shadow-[0_16px_30px_rgba(15,23,42,0.35)] transition-transform duration-200 hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-primary-soft focus:ring-offset-2 focus:ring-offset-white"
          style={{ zIndex: 40 }}
        >
          <FaPhoneAlt size={20} />
        </button>
      </div>
    </div>
  );
}
